// IntervalData.js — Fixed full script with enrichment and pagination

import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import "./Devices.css";

const IntervalData = () => {
  const [intervals, setIntervals] = useState([]);
  const [filterField, setFilterField] = useState("deviceId");
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [intervalRes, deviceRes] = await Promise.all([
          fetch("/api/intervals"),
          fetch("/api/devices")
        ]);

        const intervalData = await intervalRes.json();
        const deviceData = await deviceRes.json();

        const enriched = intervalData.map((d) => {
          const match = deviceData.find(dev => dev.deviceId === d.deviceId);
          return {
            ...d,
            meter: d.meter || match?.meter || "—",
            account: d.account || match?.account || "—",
            transformer: d.transformer || match?.transformerId || "—",
            phase: d.phase || match?.phase || "—",
          };
        });

        setIntervals(enriched);
      } catch (err) {
        console.error("Error loading interval data:", err);
      }
    };

    fetchData();
  }, []);

  const filtered = intervals
    .filter((d) =>
      d[filterField]?.toLowerCase().includes(searchText.toLowerCase())
    )
    .filter((d) => {
      const ts = new Date(d.timestamp).getTime();
      const start = startDate ? new Date(startDate).getTime() : 0;
      const end = endDate ? new Date(endDate).getTime() : Infinity;
      return ts >= start && ts <= end;
    })
    .sort((a, b) => {
      const t1 = new Date(a.timestamp).getTime();
      const t2 = new Date(b.timestamp).getTime();
      return sortNewestFirst ? t2 - t1 : t1 - t2;
    });

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const exportToCSV = () => {
    const csv = [
      ["Device ID", "Meter", "Account", "Transformer", "Phase", "Timestamp", "L1-N", "L2-N", "L1-L2", "Battery"],
      ...filtered.map(d => [
        d.deviceId,
        d.meter,
        d.account,
        d.transformer,
        d.phase,
        new Date(d.timestamp).toLocaleString("en-US", { timeZone: "America/Chicago" }),
        d.voltageL1,
        d.voltageL2,
        d.voltageLL,
        d.battery ?? ""
      ])
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "interval-data.csv";
    a.click();
  };

  return (
    <div style={{ display: "flex", background: "#eee", minHeight: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, padding: "2rem" }}>
        <h2>Interval Data</h2>

        <div style={{ marginBottom: "1rem" }}>
          <label>Filter by:{" "}
            <select value={filterField} onChange={(e) => setFilterField(e.target.value)}>
              <option value="deviceId">Device ID</option>
              <option value="meter">Meter</option>
              <option value="account">Account</option>
              <option value="transformer">Transformer</option>
              <option value="phase">Phase</option>
            </select>
          </label>
          <input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginLeft: "1rem", marginRight: "1rem" }}
          />
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          />
          <button onClick={exportToCSV} style={{ marginLeft: "1rem" }}>Export</button>
          <label style={{ marginLeft: "2rem" }}>
            <input
              type="checkbox"
              checked={sortNewestFirst}
              onChange={() => setSortNewestFirst(!sortNewestFirst)}
              style={{ marginRight: "0.25rem" }}
            />
            Show Newest First
          </label>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>{"<"}</button>
          <span style={{ margin: "0 1rem" }}>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>{">"}</button>
          <select
            style={{ marginLeft: "1rem" }}
            value={currentPage}
            onChange={(e) => setCurrentPage(Number(e.target.value))}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
          <select
            style={{ marginLeft: "1rem" }}
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n} rows</option>
            ))}
          </select>
        </div>

        <table style={{ width: "100%", background: "#fff", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#eee" }}>
              <th>Device ID</th>
              <th>Meter</th>
              <th>Account</th>
              <th>Transformer</th>
              <th>Phase</th>
              <th>Timestamp</th>
              <th>L1-N Voltage</th>
              <th>L2-N Voltage</th>
              <th>L1-L2 Voltage</th>
              <th>Battery</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((d, i) => (
              <tr key={i}>
                <td>{d.deviceId}</td>
                <td>{d.meter}</td>
                <td>{d.account}</td>
                <td>{d.transformer}</td>
                <td>{d.phase}</td>
                <td>{new Date(d.timestamp).toLocaleString("en-US", { timeZone: "America/Chicago" })}</td>
                <td>{d.voltageL1}</td>
                <td>{d.voltageL2}</td>
                <td>{d.voltageLL}</td>
                <td>{d.battery ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IntervalData;
